import { OptionsObject } from 'notistack';
import { useDebouncedSnackbar, useSnackbar } from './use-snackbar.hooks';
import { useErrorHandler } from './use-error-handler.hooks';
import { useIntl } from 'react-intl';
import { getProductRecordFromElement } from '@shared/helpers/product.helpers';
import { getRecipesUsedInElement } from '@shared/helpers/recipe.helpers';
import { ProductRecord } from '@shared/models/product.model';
import {
  ProjectID,
  OneOfSelectableElements,
  OneOfChildElements,
} from '@shared/models/project.model';
import { Recipe } from '@shared/models/recipe.model';
import { getProject, useAddElement } from '@client/store/project';
import { getRecipes } from '@client/store/recipe/recipe.hook';
import { getSelectedElement, getSelectedVersion } from '@client/store/ui';
import { useCallback } from 'react';
import { getProductsLookup } from '@client/store/product';
import { getElementName } from '@shared/helpers/element.helpers';
import {
  CLIPBOARD_WRITE_DENIED_MESSAGE,
  CLIPBOARD_WRITE_DENIED_MESSAGE_ID,
  getClipboardObject,
  isClipboardWriteDenied,
  setClipboardObject,
} from '@shared/helpers/copy-paste.helpers';
import { wrapError } from '@shared/helpers/utils.helpers';
import {
  getParentElement,
  isElement,
  isOneOfChildElements,
  isOneOfParentElements,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import { hasDefinedProperties } from '@shared/helpers/object.helpers';
import { useAppVersion } from '../providers/ConfigProvider';
import { useImportElement } from './use-import.hooks';
import { setElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import { isProductCategoryElement } from '@shared/helpers/element-category.helpers';

interface ICopyPasteElement {
  projectId: ProjectID;
  recipes: Recipe[];
  products: ProductRecord;
  element: OneOfChildElements;
  appVersion: string;
}
const SNACKBAR_OPTIONS: OptionsObject = {
  variant: 'info',
  autoHideDuration: 2000,
  preventDuplicate: true,
} as const;

const SNACKBAR_OPTIONS_ERROR: OptionsObject = {
  ...SNACKBAR_OPTIONS,
  variant: 'error',
} as const;

export const useCopyElementToClipboard = () => {
  const enqueueSnackbar = useDebouncedSnackbar();
  const errorHandler = useErrorHandler();
  const intl = useIntl();
  const appVersion = useAppVersion();
  const isValidCopyPasteElement = useIsValidCopyPasteElement();

  return useCallback(
    async (
      element: OneOfSelectableElements | undefined = getSelectedElement(),
    ): Promise<ICopyPasteElement | undefined> => {
      const project = getProject();
      const productRecord = getProductsLookup();

      if (!element || !isOneOfChildElements(element)) {
        return;
      }

      // Include soft-deleted recipes so a copied element that references one
      // still carries the recipe to the paste target.
      const recipes = getRecipes(true);
      const filteredRecipes = getRecipesUsedInElement(element, recipes);
      const products = getProductRecordFromElement(
        element,
        productRecord,
        recipes,
      );
      const copyObject: ICopyPasteElement = {
        projectId: project.id,
        recipes: filteredRecipes,
        products,
        element,
        appVersion,
      };

      // Shouldn't happen but just in case
      if (!isValidCopyPasteElement(copyObject)) {
        return;
      }

      const successMessage = `Copied Element: ${getElementName(element, products)}`;

      try {
        await setClipboardObject<ICopyPasteElement>(copyObject);
      } catch (error) {
        // Permission denial is expected; do not send it through the crash logger.
        if (isClipboardWriteDenied(error)) {
          enqueueSnackbar(
            intl.formatMessage({
              id: CLIPBOARD_WRITE_DENIED_MESSAGE_ID,
              defaultMessage: CLIPBOARD_WRITE_DENIED_MESSAGE,
            }),
            SNACKBAR_OPTIONS_ERROR,
          );
          return;
        }
        errorHandler(wrapError(error));
        return;
      }

      enqueueSnackbar(successMessage, SNACKBAR_OPTIONS);

      return copyObject;
    },
    [appVersion, enqueueSnackbar, errorHandler, intl, isValidCopyPasteElement],
  );
};

export const usePasteElementFromClipboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const isValidCopyPasteElement = useIsValidCopyPasteElement();
  const addElement = useAddElement();
  const importElement = useImportElement();

  return useCallback(async (): Promise<
    ICopyPasteElement['element'] | undefined
  > => {
    const version = getSelectedVersion();
    const selectedElement = getSelectedElement();

    if (!version) {
      throw new Error('No version available');
    }

    const target = isProductElement(selectedElement)
      ? getParentElement(version, selectedElement)
      : selectedElement;

    // Nowhere to paste
    if (!target || !isOneOfParentElements(target)) {
      enqueueSnackbar('No paste target selected', SNACKBAR_OPTIONS_ERROR);
      return;
    }
    const data = await getClipboardObject<ICopyPasteElement>();

    // Make sure data is an element to paste
    if (!isValidCopyPasteElement(data)) {
      return;
    }
    const pasteAsSibling = shouldPasteAsSibling(target, data.element);
    let element = await importElement(
      data.element,
      data.recipes,
      data.products,
    );

    const successMessage = `Pasted Element: ${getElementName(element, getProductsLookup())}`;

    const response = await addElement(target, element, {
      applyOptions: {
        ...SNACKBAR_OPTIONS,
        successMessage,
      },
      isSibling: pasteAsSibling,
      placement: pasteAsSibling ? 'after' : 'first',
    });

    if (isElement(target)) {
      // Make sure parent is expanded
      setElementExpanded(target.id, !pasteAsSibling);
    }

    return response.element;
  }, [addElement, enqueueSnackbar, importElement, isValidCopyPasteElement]);
};

const useIsValidCopyPasteElement = () => {
  const appVersion = useAppVersion();

  return useCallback(
    (data?: ICopyPasteElement): data is ICopyPasteElement => {
      return (
        !!data &&
        hasDefinedProperties(
          data,
          'projectId',
          'recipes',
          'products',
          'element',
        ) &&
        data.appVersion === appVersion // Make sure the data is not from a previous release
      );
    },
    [appVersion],
  );
};

/**
 * Check if the element should be pasted as a sibling.
 * @param target
 * @param element
 * @returns
 */
const shouldPasteAsSibling = (
  target: OneOfSelectableElements,
  element: OneOfChildElements,
) => {
  if (isProductCategoryElement(target) || isProductElement(target)) {
    return true;
  }

  // If copy and paste an element
  if (element.id === target.id) {
    return true;
  }

  return false;
};
